import {
  chakra,
  type BreadcrumbItemProps as ChakraBreadcrumbItemProps,
  defineStyle,
} from '@chakra-ui/react';
import { getValidChildren } from '@chakra-ui/react-utils';
import { cx } from '@chakra-ui/utils';
import { cloneElement, forwardRef } from 'react';

import { BreadcrumbLink } from './BreadcrumbLink';
import { BreadcrumbSeparator } from './BreadcrumbSeparator';
import { useBreadcrumbStyles } from './context';

export type BreadcrumbItemProps = ChakraBreadcrumbItemProps;

/**
 * BreadcrumbItem is used to group a breadcrumb link.
 * It renders a `li` element to denote it belongs to an order list of links.
 */
export const BreadcrumbItem = forwardRef<'li', BreadcrumbItemProps>(
  function BreadcrumbItem(props, ref) {
    const {
      isCurrentPage,
      separator,
      isLastChild,
      spacing,
      children,
      className,
      ...rest
    } = props;

    const validChildren = getValidChildren(children);

    const clones = validChildren.map((child) => {
      if (child.type === BreadcrumbLink) {
        return cloneElement(child, {
          isCurrentPage,
        });
      }

      if (child.type === BreadcrumbSeparator) {
        return cloneElement(child, {
          spacing,
          children: child.props.children || separator,
        });
      }

      return child;
    });

    const styles = useBreadcrumbStyles();

    const itemStyles = defineStyle({
      display: 'inline-flex',
      alignItems: 'center',
      ...styles.item,
    });

    const _className = cx('breadcrumb__list-item', className);

    return (
      <chakra.li ref={ref} className={_className} {...rest} __css={itemStyles}>
        {clones}
        {!isLastChild && (
          <BreadcrumbSeparator spacing={spacing}>
            {separator}
          </BreadcrumbSeparator>
        )}
      </chakra.li>
    );
  },
);
BreadcrumbItem.displayName = 'BreadcrumbItem';
